import { Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ContentConfig, PageAvailableLanguage } from '@libs/cms/models';
import { AppStateService, CepStateService, CmsLanguageService } from '@libs/shared/common';
import {
  AppError,
  NavItemModel,
  USER_ACCOUNT_ROUTE_NAME,
  USER_HOME_ROUTE_NAME,
  USER_LOGIN_ROUTE_NAME,
} from '@libs/shared/models';
import { match, MatchFunction } from 'path-to-regexp';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  first,
  map,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { CapabilityService } from '../capability';
import { NavigationService } from '../navigation';

export interface UrlCollection {
  static: Map<string, NavItemModel>;
  dynamic: [MatchFunction<object>, NavItemModel][];
}

interface AvailableLanguagesState {
  sourcePath: string;
  languages: PageAvailableLanguage[];
}

@Injectable({ providedIn: 'root' })
export class PageViewService {
  private readonly currentPath$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly error$: BehaviorSubject<AppError | undefined> = new BehaviorSubject<AppError | undefined>(undefined);
  private readonly availableLanguages$: BehaviorSubject<AvailableLanguagesState | undefined> = new BehaviorSubject<
    AvailableLanguagesState | undefined
  >(undefined);

  /**
   * Language options belong to the page they were reported for. While a navigation is pending,
   * or when loading the new page failed, the previous page's languages must not be offered.
   */
  private readonly currentAvailableLanguages$: Observable<PageAvailableLanguage[]> = combineLatest([
    this.availableLanguages$,
    this.currentPath$,
  ]).pipe(
    map(([availableLanguages, currentPath]: [AvailableLanguagesState | undefined, string]): PageAvailableLanguage[] =>
      availableLanguages?.sourcePath === currentPath ? availableLanguages.languages : []),
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly appNav$: Observable<NavItemModel>;
  private readonly pageNav$: Observable<NavItemModel>;
  private readonly urls$: BehaviorSubject<UrlCollection> = new BehaviorSubject<UrlCollection>({
    static: new Map([]),
    dynamic: [],
  });

  private readonly categories$: BehaviorSubject<Map<string, NavItemModel>> = new BehaviorSubject(new Map([]));
  private readonly navigationTreeLoaded$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private readonly currentNode$: Observable<NavItemModel | undefined>;
  private readonly breadcrumbNodes$: Observable<NavItemModel[]>;
  private readonly contentConfig$: Observable<ContentConfig>;

  public constructor(
    private readonly state: AppStateService,
    private readonly navigation: NavigationService,
    private readonly cepStateService: CepStateService,
    private readonly cmsLanguageService: CmsLanguageService,
    private readonly capabilityService: CapabilityService,
  ) {
    this.appNav$ = this.navigation.getAppNavigation().pipe(
      catchError((error: AppError) => this.handleNavError(error)),
    );

    this.pageNav$ = this.navigation.getPageNavigation().pipe(
      catchError((error: AppError) => this.handleNavError(error)),
    );

    combineLatest([this.pageNav$, this.appNav$]).pipe(
      shareReplay(1),
    ).subscribe((items) => {
      const item: NavItemModel = { children: items };
      const urls: UrlCollection = { static: new Map(), dynamic: [] };
      const categoryMap: Map<string, NavItemModel> = new Map();

      this.buildMaps(item, urls, categoryMap);

      this.urls$.next(urls);
      this.categories$.next(categoryMap);
      this.navigationTreeLoaded$.next();
    });

    this.currentNode$ = this.initCurrentNode();
    this.breadcrumbNodes$ = this.initBreadcrumbNodes();
    this.contentConfig$ = this.initContentConfig();
  }

  public getContentConfig(): Observable<ContentConfig> {
    return this.contentConfig$;
  }

  public getTree(): Observable<NavItemModel> {
    return this.pageNav$;
  }

  public getCategoryMap(): Observable<Map<string, NavItemModel>> {
    return this.categories$.asObservable();
  }

  public whenNavigationTreeLoaded(): Observable<void> {
    return this.navigationTreeLoaded$.pipe(first());
  }

  public setPath(path: string): void {
    const pathWithoutFragment: string = path.split('#')[0];

    this.cmsLanguageService.setRequestPath(pathWithoutFragment);

    if (pathWithoutFragment !== this.currentPath$.getValue()) {
      this.currentPath$.next(pathWithoutFragment);
    }
  }

  public setAvailableLanguages(availableLanguages?: PageAvailableLanguage[]): void {
    this.availableLanguages$.next(
      availableLanguages?.length
        ? {
          sourcePath: this.currentPath$.getValue(),
          languages: availableLanguages,
        }
        : undefined,
    );
  }

  public getAvailableLanguages(): Observable<PageAvailableLanguage[]> {
    return this.currentAvailableLanguages$;
  }

  /** The language switch is only offered when the backend reports more than one translation. */
  public hasMultipleAvailableLanguages(): Observable<boolean> {
    return this.currentAvailableLanguages$.pipe(
      map((languages: PageAvailableLanguage[]): boolean => languages.length > 1),
      distinctUntilChanged(),
    );
  }

  public getCurrentNode(): Observable<NavItemModel | undefined> {
    return this.currentNode$;
  }

  public getHideNavigation(): Observable<boolean | undefined> {
    return this.state.get('hidePageNavigation');
  }

  public getBreadcrumbNodes(): Observable<NavItemModel[]> {
    return this.breadcrumbNodes$;
  }

  public getError(): Observable<AppError | undefined> {
    return this.error$;
  }

  public isActivePath(path: string): Observable<boolean> {
    return this.breadcrumbNodes$.pipe(
      withLatestFrom(this.urls$),
      map(([nodes, urls]) => {
        const node: NavItemModel | undefined = this.findNodeByPath(path, urls);

        return node ? nodes.includes(node) : false;
      }),
    );
  }

  private handleNavError(error: AppError): Observable<NavItemModel> {
    console.error(error);
    this.error$.next(error);

    return of({});
  }

  private initCurrentNode(): Observable<NavItemModel | undefined> {
    return this.currentPath$.pipe(
      tap(() => void this.cepStateService.setPageTitle('')),
      switchMap(path => this.urls$.pipe(
        map(urls => this.findNodeByPath(path, urls)),
      )),
      tap(node => void this.state.set('currentPath', node?.url)),
      shareReplay(1),
    );
  }

  private initBreadcrumbNodes(): Observable<NavItemModel[]> {
    return this.currentNode$.pipe(
      map((currentNode) => {
        if (!currentNode) {
          return [];
        }

        if (currentNode.name && [USER_LOGIN_ROUTE_NAME, USER_HOME_ROUTE_NAME, USER_ACCOUNT_ROUTE_NAME].includes(currentNode.name)) {
          return [];
        }

        const breadcrumb: NavItemModel[] = [];
        let node: NavItemModel | undefined = currentNode;

        while (node !== undefined) {
          breadcrumb.unshift(node);
          node = node.parent;
        }

        return breadcrumb;
      }),
      shareReplay(1),
    );
  }

  private initContentConfig(): Observable<ContentConfig> {
    return combineLatest([
      this.currentNode$,
      this.currentPath$,
      this.state.get('page'),
      toObservable(this.capabilityService.isAvailable('platform.cmsAppConfigMode')),
    ]).pipe(
      map(([node, currentPath, page, newAppConfigEnabled]) => ({
        contentId: newAppConfigEnabled ? undefined : node?.id,
        path: newAppConfigEnabled ? node?.url ?? currentPath : undefined,
        customerNumber: page?.customerNumber,
      })),
      distinctUntilChanged((prev, current) => {
        return prev.contentId === current.contentId
          && prev.path === current.path
          && prev.customerNumber === current.customerNumber;
      }),
      shareReplay(1),
    );
  }

  private findNodeByPath(path: string, urls: UrlCollection): NavItemModel | undefined {
    // Try full path match (static paths)
    let node: NavItemModel | undefined = urls.static.get(path);

    if (node) {
      return node;
    }

    // Try to strip query parameters (static paths)
    const queryPosition: number = path.indexOf('?');

    if (queryPosition !== -1) {
      const pathWithoutQuery: string = path.substring(0, queryPosition);

      node = urls.static.get(pathWithoutQuery);

      if (node) {
        return node;
      }
    }

    // Try to match paths with arguments
    for (let i: number = 0; i < urls.dynamic.length; i++) {
      const [pathMatcherFn, item] = urls.dynamic[i];

      if (pathMatcherFn(path)) {
        return item;
      }
    }

    return undefined;
  }

  private buildMaps(item: NavItemModel, urls: UrlCollection, categoryMap: Map<string, NavItemModel>): void {
    if (item.url?.startsWith('/')) {
      if (item.url.includes(':')) {
        urls.dynamic.push([match(item.url), item]);
      } else {
        urls.static.set(item.url, item);
      }
    }

    if (item.id) {
      categoryMap.set(item.id.toString(), item);
    }

    item.children?.forEach(node => void this.buildMaps(node, urls, categoryMap));
  }
}
